<template>
  <div class="container mt-3">
    <pdl-section-header size="xl" :is-dividing="true">
      <template #content>
        <pdl-heading :level="1">{{ $t('text.account.orderHistory') }}</pdl-heading>
      </template>
    </pdl-section-header>
    <div>
      <!-- Filters & Buttons -->
      <section class="mb-4">
        <p id="section-description" v-html="$t('orderHistory.B2B.description')"></p>

        <h2 class="font-bold text-base my-4" v-html="$t('text.filterOrders')"></h2>

        <div class="grid grid-cols-12 gap-4">
          <div class="col-span-12 md:col-span-5 lg:col-span-3">
            <pdl-form-group
              v-model="options.orderNumber"
              type="text"
              name="orderNumber"
              :label="$t('text.account.orderHistory.orderNumber')"
              :maxlength="8"
              qaid="orderHistoryOrderNumber"
            />

            <pdl-form-group
              v-model="options.invoiceNumber"
              type="text"
              name="invoiceNumber"
              :label="$t('text.invoiceNumber')"
              :maxlength="8"
              qaid="orderHistoryInvoiceNumber"
            />

            <pdl-form-group
              v-model="options.partNumber"
              type="text"
              name="partNumber"
              :label="$t('text.partNumber')"
              :maxlength="25"
              qaid="orderHistoryPartNumber"
            />

            <pdl-form-group
              v-model="options.customerPo"
              type="text"
              name="customerPo"
              :label="$t('text.customerPo')"
              :maxlength="25"
              qaid="orderHistoryCustomerPo"
            />

            <pdl-form-group
              v-model="options.shipToNumber"
              type="text"
              name="shipToNumber"
              :label="$t('text.storeNumber')"
              :maxlength="8"
              qaid="orderHistoryStoreNumber"
              @update:modelValue="updateShipToValue"
            />
          </div>
          <div class="col-span-12 md:col-span-7 lg:col-span-6 md:flex">
            <fieldset class="form-fieldset mr-4">
              <pdl-form-group name="orderDate" :label="$t('text.orderDate')">
                <template #input>
                  <pdl-date-picker
                    id="order-date-input"
                    ref="orderPicker"
                    qaid="order-date-input"
                    :allow-input="true"
                    :label="$t('order.orderDate.select')"
                    mode="range"
                    @update:modelValue="orderDateInput"
                  />
                </template>
              </pdl-form-group>

              <pdl-form-group name="orderDate" :label="$t('text.invoiceDate')">
                <template #input>
                  <pdl-date-picker
                    id="invoice-date-input"
                    ref="invoicePicker"
                    qaid="invoice-date-input"
                    :allow-input="true"
                    :label="$t('order.invoiceDate.select')"
                    mode="range"
                    @update:modelValue="invoiceDateInput"
                  />
                </template>
              </pdl-form-group>
            </fieldset>

            <fieldset id="credit-memos-field" class="form-fieldset">
              <pdl-form-group
                type="radio"
                name="creditMemos"
                qaid="credit-memos-filter-radio"
                :label="$t('text.creditMemos')"
                :multi-items="creditMemosOptions"
                :model-value="options.creditMemoFilter"
                @update:modelValue="handleCreditMemoUpdate"
              />

              <pdl-form-group
                type="radio"
                name="orders"
                qaid="order-filter-radio"
                :label="$t('text.orders')"
                :multi-items="orderOptions"
                :model-value="options.productTypeFilter"
                @update:modelValue="handleOrderUpdate"
              />

              <pdl-form-group
                class="lg:hidden"
                type="radio"
                name="orderStatusMobile"
                qaid="order-status-filter-radio"
                :label="$t('text.account.orderHistory.orderStatus')"
                :multi-items="orderStatusOptions"
                :model-value="options.orderStatusFilter"
                @update:modelValue="handleOrderStatusUpdate"
              />
            </fieldset>
          </div>
          <div class="col-span-12 lg:col-span-3">
            <fieldset id="order-status-field" class="form-fieldset hidden lg:block">
              <pdl-form-group
                type="radio"
                name="orderStatus"
                qaid="order-status-filter-radio"
                :label="$t('text.account.orderHistory.orderStatus')"
                :multi-items="orderStatusOptions"
                :model-value="options.orderStatusFilter"
                @update:modelValue="handleOrderStatusUpdate"
              />
            </fieldset>
            <div class="buttons buttons--right-for-md">
              <trek-button
                id="filter-orders-button"
                primary
                class="lg:w-full"
                qaid="filter-orders-button"
                @click="fetchAllOrders"
              >
                <span v-html="$t('text.filterOrders')"></span>
              </trek-button>
              <trek-button
                id="clear-filters-button"
                secondary
                class="lg:w-full"
                qaid="clear-filters-button"
                @click="clearChanges"
              >
                <span v-html="$t('text.clearFilters')"></span>
              </trek-button>
            </div>
          </div>
        </div>
      </section>

      <pdl-loading page :is-loading="!isContentLoaded" />
      <!-- Orders Tabs -->
      <section v-if="isContentLoaded" id="order-tabs" class="my-5">
        <pdl-tabs id="order-tabs" ref="tabs" v-model="selectedTab">
          <pdl-tab-pane v-for="tab in tabs" :key="`${tab.key}`" :name="tab.id">
            <template #label>
              <h3 class="heading heading--md m-0">
                <span>{{ $t(tab.label) }}</span>
                <span>({{ orderData[tab.key].totalItemCount }})</span>
              </h3>
            </template>

            <order-history-grid
              :caption="tab.label"
              :grid-data="orderData[tab.key]"
              :columns="columns"
              :part-search-columns="partSearchColumns"
              :order-status="tab.key"
              :child-columns="childColumns"
              :child-data-key="childDataKey"
              :link-manage-allocated-items="linkManageAllocatedItems"
              :is-part-search="isPartSearch"
              @expand-order="expandOrder"
            ></order-history-grid>
          </pdl-tab-pane>
        </pdl-tabs>
      </section>
    </div>
  </div>
</template>

<script>
import storefrontInstance from '@/api/instances/storefront';
import pickBy from 'lodash/pickBy';
import {Url} from '@/utils/url';
import {PdlSectionHeader, PdlHeading} from '@pedal/pdl-section-header';
import {PdlDatePicker} from '@pedal/pdl-date-picker';
import OrderHistoryGrid from '@/components/containers/my-account/OrderHistoryGrid';
import TrekButton from '@/components/TrekButton';
import {PdlLoading} from '@pedal/pdl-loading';
import {PdlFormGroup} from '@pedal/pdl-form-group';
import {PdlTabs, PdlTabPane} from '@pedal/pdl-tabs';
import dayjs from 'dayjs';
import {DateFormats} from '@/constants/date-formats';
import {cloneRaw} from '@/utils/util';

const ALL = 'All';
const INITIAL_OPT = {
  pageSize: 10,
  creditMemoFilter: ALL,
  invoiced: null,
  invoiceDateStart: null,
  invoiceDateEnd: null,
  onlyCreditMemos: null,
  orderDateStart: null,
  orderDateEnd: null,
  orderNumber: null,
  invoiceNumber: null,
  partNumber: null,
  productTypeFilter: ALL,
  customerPo: null,
  shipToNumber: null,
  orderStatusFilter: ALL,
};

export default {
  components: {
    PdlLoading,
    OrderHistoryGrid,
    PdlSectionHeader,
    PdlHeading,
    PdlDatePicker,
    PdlFormGroup,
    PdlTabs,
    PdlTabPane,
    TrekButton,
  },

  provide() {
    return {
      container: this,
    };
  },
  props: {
    isCartCheckout: {
      type: Boolean,
      default: false,
    },
    columns: {
      type: Array,
      default() {
        return [];
      },
    },
    partSearchColumns: {
      type: Array,
      default() {
        return [];
      },
    },
    idKey: {
      type: String,
      default: undefined,
    },
    childColumns: {
      type: Array,
      default() {
        return [];
      },
    },
    childDataKey: {
      type: String,
      default: undefined,
    },
    linkManageAllocatedItems: {
      type: String,
      default: undefined,
    },
    initialOrderNumber: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      openOrders: {rows: [], pageIndex: 0, pageCount: 0, totalItemCount: 0},
      pastOrders: {rows: [], pageIndex: 0, pageCount: 0, totalItemCount: 0},
      pageSize: 10,
      options: cloneRaw(INITIAL_OPT),
      initialOptions: cloneRaw(INITIAL_OPT),
      isPartSearch: false,
      urlParams: {},
      localSelectedTab: '',
      isContentLoaded: false,
    };
  },
  computed: {
    selectedTab: {
      get() {
        return this.localSelectedTab;
      },
      set(val) {
        //tabs is destroyed when isContentLoaded is set to false
        //while tab panes unmount, they emit update:modelValue to change the value to the next pane
        //we do NOT want to update the model value at that point, since every tab is being removed
        if (!this.isContentLoaded) return;
        this.localSelectedTab = val;
      },
    },
    creditMemosOptions() {
      return [
        {value: ALL, displayName: this.$t('text.showAllOrders')},
        {value: 'OnlyCreditMemos', displayName: this.$t('text.creditMemos')},
        {value: 'HideCreditMemos', displayName: this.$t('orderHistory.B2B.hide.creditMemos')},
      ];
    },

    orderOptions() {
      return [
        {value: ALL, displayName: this.$t('text.showAllOrders')},
        {value: 'ProjectOneBikes', displayName: this.$t('text.P1Orders')},
        {value: 'Consumer', displayName: this.$t('text.consumerOrders')},
        {value: 'Bikes', displayName: this.$t('backordersAllocated.B2B.bikes')},
        {value: 'Accessories', displayName: this.$t('text.accessories')},
      ];
    },

    orderStatusOptions() {
      return [
        {value: ALL, displayName: this.$t('text.showAllOrders')},
        {value: 'WithAllocatedItems', displayName: this.$t('orderHistory.B2B.ordersAllocatedItems')},
        {value: 'InWarehouse', displayName: this.$t('orderHistory.B2B.inWarehouse')},
        {value: 'Processing', displayName: this.$t('orderHistory.B2B.itemsProcessing')},
      ];
    },

    openOrdersData() {
      return this.openOrders;
    },

    orderData() {
      return {
        openOrders: this.openOrders,
        pastOrders: this.pastOrders,
      };
    },

    tabs() {
      const tabs = [
        {
          id: 'tab-past-orders',
          label: 'text.invoicedOrders',
          data: this.pastOrders,
          key: 'pastOrders',
        },
      ];
      if (this.isCartCheckout) {
        tabs.unshift({
          id: 'tab-open-orders',
          label: 'text.openOrders',
          data: this.openOrders,
          key: 'openOrders',
        });
      }
      return tabs;
    },
  },

  async created() {
    this.setFiltersFromUrlParams();
    this.options.orderNumber = this.initialOrderNumber ? this.initialOrderNumber : this.options.orderNumber;
    await this.fetchAllOrders();
    //set selectedTab to first tab with content on the first load
    this.selectedTab = this.isCartCheckout && this.openOrders.rows.length ? 'tab-open-orders' : 'tab-past-orders';
  },

  methods: {
    handleCreditMemoUpdate(value) {
      this.options['creditMemoFilter'] = value;
    },

    handleOrderUpdate(value) {
      this.options['productTypeFilter'] = value;
    },

    handleOrderStatusUpdate(value) {
      this.options['orderStatusFilter'] = value;
    },

    clearChanges() {
      this.options = cloneRaw(this.initialOptions);
      this.$refs.orderPicker.fp.clear();
      this.$refs.invoicePicker.fp.clear();
      this.fetchAllOrders();
    },

    async fetchAllOrders() {
      try {
        this.isContentLoaded = false;
        await Promise.all([this.fetchOrders('openOrders'), this.fetchOrders('pastOrders')]);
      } catch (error) {
        console.error(error);
      } finally {
        this.isContentLoaded = true;
      }
    },

    async fetchOrders(status = 'openOrders') {
      let param = {
        pageIndex: this[status].pageIndex,
        ...this.options,
      };
      param = pickBy(param); // Removing null properties
      Url.removeUrlParameterObjectFromUrl(this.options);
      Url.appendObjectToUrlParameters(this.options);

      try {
        const {
          data: {
            data: {orders, paging},
          },
        } = await storefrontInstance.get(`/my-account/${status.toString().toLowerCase()}`, {
          params: new URLSearchParams(param),
        });

        this[status] = {
          rows: orders.map((row) => ({
            ...row,
            expanded: row.expanded === undefined ? false : row.expanded,
            allocatedItems: row.allocatedItems || [],
            notAllocatedItems: row.notAllocatedItems || [],
          })),
          pageIndex: paging.pageIndex || 0,
          pageCount: paging.pageCount || 0,
          totalItemCount: paging.totalItemCount || 0,
        };
      } catch (error) {
        console.error(error);
        return {rows: [], totalItemCount: 0};
      } finally {
        this.isPartSearch = this.options.partNumber?.length > 0;
      }
    },

    invoiceDateInput({startDate, endDate}) {
      if (startDate) {
        this.options.invoiceDateStart = dayjs(startDate).format(DateFormats.MM_DD_YYYY);
      } else {
        this.options.invoiceDateStart = null;
      }
      if (endDate) {
        this.options.invoiceDateEnd = dayjs(endDate).format(DateFormats.MM_DD_YYYY);
      } else {
        this.options.invoiceDateEnd = null;
      }
    },

    orderDateInput({startDate, endDate}) {
      if (startDate) {
        this.options.orderDateStart = dayjs(startDate).format(DateFormats.MM_DD_YYYY);
      } else {
        this.options.orderDateStart = null;
      }
      if (endDate) {
        this.options.orderDateEnd = dayjs(endDate).format(DateFormats.MM_DD_YYYY);
      } else {
        this.options.orderDateEnd = null;
      }
    },
    updateShipToValue() {
      this.options.shipToNumber = this.options.shipToNumber.replace(/[^\d]/g, '');
    },
    setFiltersFromUrlParams() {
      this.urlParams = Url.parse(window.location.search);
      for (const urlParameter in this.urlParams) {
        for (const filterOption in this.options) {
          if (urlParameter === filterOption && this.urlParams[urlParameter] !== null) {
            //url only uses strings, so we need to convert string booleans back to booleans
            if (this.urlParams[urlParameter] === 'true') {
              this.urlParams[urlParameter] = true;
            } else if (this.urlParams[urlParameter] === 'false') {
              this.urlParams[urlParameter] = false;
            }
            this.options[urlParameter] = this.urlParams[urlParameter];
          }
        }
      }
    },
    expandOrder({rowIndex, isExpanded, orderType}) {
      this[orderType].rows[rowIndex].expanded = isExpanded;
    },
  },
};
</script>
